/**
 * Company administration. Owner-only for creation; a company admin sees and
 * edits their own.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Building2, Check, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../../api/client';
import { Link } from 'react-router-dom';
import { canSeeAllCompanies, useAuth } from '../../store/auth';
import type { Company, Office, Page, ShiftPolicy } from '../../types';

export default function CompaniesPage() {
  const me = useAuth((s) => s.user);
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  /**
   * Every company's offices and policies, in two requests rather than two per
   * card. skipActingCompany for the same reason the company list uses it:
   * this screen is about all of them, and narrowing to the acting company
   * would make every other card look unconfigured.
   */
  const { data: offices } = useQuery<Page<Office>>({
    queryKey: ['setup-offices'],
    queryFn: async () =>
      (
        await client.get('/api/v1/admin/offices', {
          skipActingCompany: true,
          params: { limit: 200 },
        })
      ).data,
  });

  const { data: policies } = useQuery<Page<ShiftPolicy>>({
    queryKey: ['setup-policies'],
    queryFn: async () =>
      (
        await client.get('/api/v1/admin/shift-policies', {
          skipActingCompany: true,
          params: { limit: 200 },
        })
      ).data,
  });

  const { data, isLoading } = useQuery<Page<Company>>({
    queryKey: ['admin-companies'],
    queryFn: async () =>
      (
        // Listing companies must never be narrowed to one company.
        await client.get('/api/v1/admin/companies', {
          skipActingCompany: true,
          params: { limit: 200, includeInactive: true },
        })
      ).data,
  });

  const createCompany = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await client.post('/api/v1/admin/companies', body)).data,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
      // The card's checklist reads these, and a company created a second ago
      // has neither — without this the new card renders complete until
      // something else refetches.
      queryClient.invalidateQueries({ queryKey: ['setup-offices'] });
      queryClient.invalidateQueries({ queryKey: ['setup-policies'] });
      setShowForm(false);
      // Not "created" and nothing else. A company with no workplace and no
      // monitoring policy cannot take a hire without dropping them into
      // hardcoded times, and the moment somebody has just made one is the
      // moment to say so.
      toast.success(`${data.name} created — it still needs setting up`);
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not create the company')),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">
          Companies{' '}
          {data && <span className="badge bg-gray-100 text-gray-600">{data.total}</span>}
        </h2>
        {canSeeAllCompanies(me) && (
          <button type="button" className="btn-primary" onClick={() => setShowForm((v) => !v)}>
            <Plus className="h-4 w-4" />
            {showForm ? 'Cancel' : 'Add company'}
          </button>
        )}
      </div>

      {showForm && (
        <CompanyForm submitting={createCompany.isPending} onSubmit={createCompany.mutate} />
      )}

      {isLoading ? (
        <div className="flex justify-center py-10 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {data?.items.map((company) => (
            <CompanyCard
              key={company.id}
              company={company}
              offices={
                offices?.items.filter((o) => o.companyId === company.id).length
              }
              policies={
                policies?.items.filter((p) => p.companyId === company.id).length
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CompanyCard({
  company,
  offices,
  policies,
}: {
  company: Company;
  /** Undefined while loading — distinct from zero, which is the problem. */
  offices: number | undefined;
  policies: number | undefined;
}) {
  const { data: stats } = useQuery({
    queryKey: ['company-stats', company.id],
    queryFn: async () =>
      (await client.get(`/api/v1/admin/companies/${company.id}/stats`)).data,
  });

  return (
    <div className="card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 font-medium text-gray-900">
            <Building2 className="h-4 w-4 text-indigo-600" />
            {company.name}
          </h3>
          <p className="mt-0.5 text-sm text-gray-500">
            {[company.city, company.state].filter(Boolean).join(', ') || '—'}
          </p>
        </div>
        {!company.isActive && (
          <span className="badge bg-gray-200 text-gray-600">inactive</span>
        )}
      </div>

      <SetupChecklist company={company} offices={offices} policies={policies} />

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <dt className="text-gray-500">GSTIN</dt>
        <dd className="font-mono text-xs text-gray-700">{company.gstin ?? '—'}</dd>
        <dt className="text-gray-500">PAN</dt>
        <dd className="font-mono text-xs text-gray-700">{company.pan ?? '—'}</dd>
        <dt className="text-gray-500">Bank</dt>
        <dd className="font-mono text-xs text-gray-700">
          {company.bankAccountNumberMasked ?? '—'}
        </dd>
      </dl>

      {stats && (
        <div className="mt-3 flex flex-wrap gap-3 border-t border-gray-100 pt-3 text-xs text-gray-600">
          <span>{stats.userCount} users</span>
          <span>{stats.activeEmployeeCount} active staff</span>
          <span>{stats.clientCount} clients</span>
          <span>{stats.placementCount} placements</span>
        </div>
      )}

      {!company.state && (
        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
          No state set. Professional Tax slabs are selected by state, so payroll
          cannot run for this company until it is filled in.
        </p>
      )}
    </div>
  );
}

function CompanyForm({
  submitting,
  onSubmit,
}: {
  submitting: boolean;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [form, setForm] = useState({
    name: '',
    legalName: '',
    gstin: '',
    pan: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    bankName: '',
    bankAccountNumber: '',
    bankIfsc: '',
  });

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [k]: e.target.value });

  return (
    <form
      className="card grid gap-3 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        // Blank optional fields are sent as null so validators skip them
        // rather than rejecting an empty string.
        const body = Object.fromEntries(
          Object.entries(form).map(([k, v]) => [k, v.trim() || null]),
        );
        body.name = form.name.trim();
        onSubmit(body);
      }}
    >
      <div>
        <label className="label" htmlFor="c-name">Company name</label>
        <input spellCheck={false} id="c-name" className="input" value={form.name} onChange={set('name')} required />
      </div>
      <div>
        <label className="label" htmlFor="c-legal">Legal name</label>
        <input spellCheck={false} id="c-legal" className="input" value={form.legalName} onChange={set('legalName')} />
      </div>
      <div>
        <label className="label" htmlFor="c-gstin">GSTIN</label>
        <input
          spellCheck={false}
          id="c-gstin"
          className="input font-mono"
          placeholder="33ABCDE1234F1Z5"
          value={form.gstin}
          onChange={set('gstin')}
        />
      </div>
      <div>
        <label className="label" htmlFor="c-pan">PAN</label>
        <input
          spellCheck={false}
          id="c-pan"
          className="input font-mono"
          placeholder="ABCDE1234F"
          value={form.pan}
          onChange={set('pan')}
        />
      </div>
      <div className="sm:col-span-2">
        <label className="label" htmlFor="c-address">Address</label>
        <textarea
          spellCheck={false}
          id="c-address"
          className="input"
          rows={2}
          maxLength={1000}
          value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
        />
      </div>
      <div>
        <label className="label" htmlFor="c-city">City</label>
        <input spellCheck={false} id="c-city" className="input" value={form.city} onChange={set('city')} />
      </div>
      <div>
        <label className="label" htmlFor="c-state">
          State <span className="text-amber-600">(required for payroll)</span>
        </label>
        <input
          spellCheck={false}
          id="c-state"
          className="input"
          placeholder="Telangana"
          value={form.state}
          onChange={set('state')}
        />
      </div>
      <div>
        <label className="label" htmlFor="c-bank">Bank name</label>
        <input
          spellCheck={false}
          id="c-bank"
          className="input"
          placeholder="HDFC Bank"
          value={form.bankName}
          onChange={set('bankName')}
        />
      </div>
      <div>
        <label className="label" htmlFor="c-acct">Bank account</label>
        <input
          spellCheck={false}
          id="c-acct"
          className="input font-mono"
          value={form.bankAccountNumber}
          onChange={set('bankAccountNumber')}
        />
      </div>
      <div>
        <label className="label" htmlFor="c-ifsc">IFSC</label>
        <input
          spellCheck={false}
          id="c-ifsc"
          className="input font-mono"
          placeholder="HDFC0001234"
          value={form.bankIfsc}
          onChange={set('bankIfsc')}
        />
      </div>

      <div className="sm:col-span-2">
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          Create company
        </button>
      </div>
    </form>
  );
}

/**
 * What a company still needs before anybody can be hired into it.
 *
 * Creating a company creates nothing else — no workplace, no monitoring
 * policy — and nothing anywhere said so. The first user created into one
 * lands with a null policy, which puts them outside the attendance report
 * entirely and schedules their pings against hardcoded literals in
 * presence.py. That is how Metro Staffing Solutions ended up with two
 * employees and neither.
 *
 * Shown on the card rather than only after creation, because the companies
 * already in this state were created before any of this existed and a
 * one-time prompt would never reach them.
 *
 * It states what is missing and links to where it is fixed. It does not fix
 * anything itself: an office carries geolocation that has to be set per
 * office and can never be guessed, and the two monitoring policies differ on
 * whether people are geofenced — which is a decision, not a default.
 */
function SetupChecklist({
  company,
  offices,
  policies,
}: {
  company: Company;
  offices: number | undefined;
  policies: number | undefined;
}) {
  const actingCompanyId = useAuth((s) => s.actingCompanyId);

  // Still loading. Saying "no workplace" before the answer arrives would
  // accuse a configured company of being broken for as long as the request
  // takes.
  if (offices === undefined || policies === undefined) return null;
  if (offices > 0 && policies > 0) {
    return (
      <p className="mt-3 flex items-center gap-1.5 text-xs text-gray-400">
        <Check className="h-3.5 w-3.5" />
        {offices} workplace{offices === 1 ? '' : 's'} · {policies} monitoring
        {' '}polic{policies === 1 ? 'y' : 'ies'}
      </p>
    );
  }

  const missing = [
    offices === 0 ? 'a workplace' : null,
    policies === 0 ? 'an attendance monitoring policy' : null,
  ].filter(Boolean);

  return (
    <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2">
      <p className="flex items-start gap-1.5 text-xs text-amber-900">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Not set up yet — this company has no {missing.join(' and no ')}.
          Anyone hired into it now runs against hardcoded 08:30–17:30 times
          with nothing recorded about which, and sits outside the attendance
          report.
        </span>
      </p>
      {/* Workplaces reads the acting company, so an owner has to be acting as
          this one to configure it. Saying which rather than sending them to a
          screen showing somebody else's rows. */}
      <p className="mt-1.5 pl-5 text-xs text-amber-800">
        {actingCompanyId === company.id ? (
          <Link to="/admin/workplaces" className="underline hover:text-amber-900">
            Set it up under Workplaces
          </Link>
        ) : (
          <>Switch to {company.name} in the company picker, then set it up
          under Workplaces.</>
        )}
      </p>
    </div>
  );
}
